import { Avatar, Divider, Menu, ScrollArea, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCheck, IconLogout2 } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import type { LoginResponse } from '@/services/api/api.types'
import { navigateTo } from '@/utils/navigation'
import { showChannelSwitchError } from '@/utils/sponsors'

import { SponsorSwitchConfirmationModal } from './SponsorSwitchConfirmationModal.tsx'

type AvailableSponsor = NonNullable<LoginResponse['availableSponsors']>[0]

// Generate a consistent color for a string using a simple hash
function stringToColor(str: string): string {
  const colors = [
    'blue',
    'cyan',
    'grape',
    'green',
    'indigo',
    'lime',
    'orange',
    'pink',
    'red',
    'teal',
    'violet',
    'yellow',
  ]

  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

export const AccountMenu = observer(function AccountMenu() {
  const { authenticationStore, uiStore, api } = useGlobalState()
  const { user, availableSponsors, logOut } = authenticationStore
  const [confirmationModalOpened, { open: openConfirmationModal, close: closeConfirmationModal }] = useDisclosure(false)
  const [selectedSponsor, setSelectedSponsor] = useState<AvailableSponsor | null>(null)

  const currentSponsorId = user?.sponsorId
  const email = user?.email ?? 'Unknown User'
  const avatarLetter = email.charAt(0).toUpperCase()
  const avatarColor = stringToColor(email)

  // Fetch sponsor list to get proper sponsor names
  const sponsorQuery = useQuery({
    queryKey: ['sponsor-list', api.baseUrl],
    queryFn: () => api.getSponsorList(),
    enabled: !!user?.sponsorId,
  })

  // Build accessible sponsor IDs and name map from API response
  // The sponsor-list endpoint only returns sponsors where the user has portal access
  const { accessibleSponsorIds, sponsorNameMap } = useMemo(() => {
    const ids = new Set<string>()
    const nameMap = new Map<string, string>()

    if (sponsorQuery.data?.sponsors) {
      sponsorQuery.data.sponsors.forEach(sponsor => {
        ids.add(sponsor.sponsorId)
        nameMap.set(sponsor.sponsorId, sponsor.sponsorName)
      })
    }

    return { accessibleSponsorIds: ids, sponsorNameMap: nameMap }
  }, [sponsorQuery.data])

  // Build display list: filter availableSponsors to only those with portal access
  const displaySponsors = useMemo(() => {
    if (!availableSponsors || availableSponsors.length === 0) return []

    // Filter to only show sponsors that the user has portal access to
    return availableSponsors
      .filter(sponsor => accessibleSponsorIds.has(sponsor.sponsorId))
      .map(sponsor => ({
        ...sponsor,
        displayName: sponsorNameMap.get(sponsor.sponsorId) || sponsor.environmentName,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [availableSponsors, accessibleSponsorIds, sponsorNameMap])

  async function handleLogout() {
    uiStore.showLoadingOverlay()
    await logOut()
  }

  function handleSponsorClick(sponsor: AvailableSponsor) {
    // If clicking the current sponsor, do nothing
    if (sponsor.sponsorId === currentSponsorId) {
      return
    }

    // Open confirmation modal with the selected sponsor
    setSelectedSponsor(sponsor)
    openConfirmationModal()
  }

  async function handleConfirmSponsorSwitch() {
    if (!selectedSponsor) return

    const channelName = sponsorNameMap.get(selectedSponsor.sponsorId) || selectedSponsor.environmentName

    closeConfirmationModal()
    uiStore.showLoadingOverlay()

    try {
      const response = await authenticationStore.selectSponsor(selectedSponsor)

      if (response.ok) {
        // Navigate to dashboard after successful sponsor switch to ensure fresh data
        navigateTo('dashboard')
      } else {
        console.error('Failed to switch sponsor:', response.message, response.error)
        showChannelSwitchError(channelName, response)
      }
    } catch (error) {
      console.error('Error switching sponsor:', error)
      showChannelSwitchError(channelName, error)
    } finally {
      uiStore.hideLoadingOverlay()
      setSelectedSponsor(null)
    }
  }

  function handleCancelSponsorSwitch() {
    setSelectedSponsor(null)
    closeConfirmationModal()
  }

  const selectedSponsorName = selectedSponsor
    ? sponsorNameMap.get(selectedSponsor.sponsorId) || selectedSponsor.environmentName
    : ''

  return (
    <>
      <Menu shadow="md" width={280} position="bottom-end">
        <Menu.Target>
          <Avatar
            color={avatarColor}
            radius="xl"
            size="md"
            style={{ cursor: 'pointer' }}
            alt="Account menu"
            role="button">
            {avatarLetter}
          </Avatar>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>
            <Text size="sm" fw={500} truncate>
              {email}
            </Text>
          </Menu.Label>

          {displaySponsors.length > 0 && (
            <>
              <Divider />
              <Menu.Label>Channels</Menu.Label>
              <ScrollArea.Autosize mah="50vh" type="auto">
                {displaySponsors.map(sponsor => (
                  <Menu.Item
                    key={sponsor.sponsorId}
                    onClick={() => handleSponsorClick(sponsor)}
                    rightSection={
                      sponsor.sponsorId === currentSponsorId ? <IconCheck size={16} stroke={2} /> : undefined
                    }>
                    {sponsor.displayName}
                  </Menu.Item>
                ))}
              </ScrollArea.Autosize>
            </>
          )}

          <Divider />

          <Menu.Item leftSection={<IconLogout2 size={16} />} color="red" onClick={() => void handleLogout()}>
            Logout
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <SponsorSwitchConfirmationModal
        opened={confirmationModalOpened}
        onClose={handleCancelSponsorSwitch}
        onConfirm={() => void handleConfirmSponsorSwitch()}
        sponsorName={selectedSponsorName}
      />
    </>
  )
})
